<script setup lang="ts">
import { watch } from 'vue'

import { IncidentAttributeId } from '@collector/sportsapi-client-legacy'
import Button from '@mobile/components/Button/Button.vue'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'

import { AttributeConfig } from '../../Sports/composables/useSportOptions'
import {
  getAttributeId,
  isAttributeConfig,
} from '../../Sports/composables/useSportOptions/types'
import GroupTitle from './GroupTitle.vue'

interface Props {
  name: string
  important?: boolean
  isFulfilled?: boolean
  attributesOrAttributeConfigs: (
    IncidentAttributeId | AttributeConfig<IncidentAttributeId>
  )[]
  cols: number
  linkedIncident: ProbableLinkedIncident
  selectedAttributes: readonly IncidentAttributeId[]
}

const props = defineProps<Props>()

// emits
const emit = defineEmits<{
  attributeSelected: [attributeId: IncidentAttributeId]
}>()

// methods
function isSelectedAttribute(attributeId: IncidentAttributeId): boolean {
  return props.selectedAttributes.includes(attributeId)
}

function handleClick(attributeId: IncidentAttributeId): void {
  emit('attributeSelected', attributeId)
}

function getAttributeName(attributeId: IncidentAttributeId): string {
  return (
    props.linkedIncident.sportIncident.attributes.find(
      ({ id }) => id === attributeId,
    )?.name ?? 'N/A'
  )
}

watch(
  () => props.selectedAttributes,
  (selected: readonly IncidentAttributeId[]) => {
    for (const attr of selected) {
      const attrConfig = props.attributesOrAttributeConfigs.find(
        (a) => isAttributeConfig(a) && getAttributeId(a) === attr,
      )
      if (
        attrConfig &&
        isAttributeConfig(attrConfig) &&
        !attrConfig.isActive(selected)
      ) {
        // Emit deselect for this attribute
        emit('attributeSelected', attr)
      }
    }
  },
  { immediate: true, deep: true },
)
</script>

<template>
  <div>
    <GroupTitle
      :name
      :important
      :isFulfilled
    />
    <div :class="`grid gap-2 grid-cols-${cols} mb-3`">
      <template
        v-for="attr in attributesOrAttributeConfigs"
        :key="getAttributeId(attr)"
      >
        <Button
          :color="isSelectedAttribute(getAttributeId(attr)) ? 'blue' : 'white'"
          :size="'lg'"
          :disabled="
            isAttributeConfig(attr) ? !attr.isActive(selectedAttributes) : false
          "
          @click="handleClick(getAttributeId(attr))"
        >
          {{ getAttributeName(getAttributeId(attr)) }}
        </Button>
      </template>
    </div>
  </div>
</template>
